// МЧМ.1 / АНИМ.5: экран матчмейкинга v2.
//
// 4 фазы (вместо плоского found+countdown):
//   1. scan      — радар-карта сети + пульсирующие кольца + точки-узлы.
//                  Длится до match_found (mock: 4-9с симуляция, hard-cap
//                  60с casual / 90с rated/war).
//   2. lock      — радар фиксируется на одной точке, она «втягивается» к
//                  центру и становится аватаром противника (0.8с).
//   3. vs        — аватары обоих игроков съезжаются к центру, появляется
//                  «VS» с глитч-эффектом (1.2с).
//   4. countdown — «3 → 2 → 1» с глитчем (3с), потом heavy_glitch в
//                  /battle через useScreenTransition.
//
// 3 режима (modes):
//   - casual    — циан палитра
//   - rated     — амбер
//   - war       — красный + дополнительный stake-box с USDT-данными
//
// Hard-cap: 60с casual, 90с rated/war (см. ответ на open question §3).
// При cap → toast «Сеть тихая» + возврат на /pvp.
//
// ПвП.4b: реальный matchmaking через Colyseus. На сервере /matchmake/
// joinOrCreate/match создаёт seat reservation + WS-комнату. Когда в
// state.players оказывается 2 игрока (живой или бот через 30с) — фаза
// lock→vs→countdown→navigate в /pvp/battle (ColyseusTestScreen) с
// прокинутой комнатой через pvp/client.ts singleton.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Room } from '@colyseus/sdk';
import { ScreenLayout } from '../components/ScreenLayout';
import { useGameStore } from '../store/useGameStore';
import { PlayerName } from '../components/PlayerName';
import { UserAvatar } from '../components/UserAvatar';
import { useTr } from '../i18n/useTr';
import { trLeague } from '../i18n/serverStrings';
import { useScreenTransition } from '../transitions';
import { joinOrCreateMatch, setCurrentRoom } from '../pvp/client';
import { play } from '../audio/sfx';
import styles from './MatchmakingScreen.module.css';

type ModeId = 'casual' | 'rated' | 'war';
type Phase = 'scan' | 'lock' | 'vs' | 'countdown';

interface LocationState {
  modes: ModeId[];
  warStake: number | null;          // legacy: max выбранной — для UI блока депозита
  warStakes?: number[];              // МЧМ.3: массив выбранных ставок Войны
  deckId: number | null;
  deckName: string;
  // ПвП.4b: передаются из PvpScreen — фракция/Герой выбранной колоды.
  heroId?: string;
  faction?: 'deus' | 'syntetics' | 'global' | 'livlife' | 'neutral';
}

// МЧМ.3: одна очередь матчмейкинга. Несколько таких работают параллельно.
interface QueueSpec {
  mode: ModeId;          // 'casual' | 'rated' | 'war'
  serverMode: string;    // 'casual' | 'rated' | 'war:5' etc — что шлём серверу
  stake: number;          // 0 для не-war
  league: string;        // bucket для filterBy на сервере
  priority: number;       // больше = выше приоритет при race
}

// ПвП.4b: FAKE_OPPONENTS удалили — теперь оппонент берётся из state.players
// Colyseus-комнаты. FACTION_LABELS остаётся для fallback в factionToLabel().
const FACTION_LABELS = ['Деус', 'Синтетикс', 'Глобал', 'Лив Лайф'];
const FACTION_COLORS: Record<string, string> = {
  'Деус': '#3D7DFF', 'Синтетикс': '#C93DFF',
  'Глобал': '#FF8A1F', 'Лив Лайф': '#FF3D3D'
};

// Hard-cap для каждого режима (в секундах). Если превышен — выход.
const HARD_CAP_S: Record<ModeId, number> = {
  casual: 60,
  rated: 90,
  war: 90
};

function modeLabel(id: ModeId, warStake: number | null, isEn: boolean): string {
  if (id === 'casual') return isEn ? 'Casual' : 'Обычный';
  if (id === 'rated')  return isEn ? 'Ranked' : 'Рейтинг';
  return warStake
    ? (isEn ? `War ${warStake} USDT` : `Война ${warStake} USDT`)
    : (isEn ? 'War' : 'Война');
}

export function MatchmakingScreen() {
  const navigate = useNavigate();
  const tx = useScreenTransition();
  const location = useLocation();
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  const player = useGameStore(s => s.player);
  const progress = useGameStore(s => s.progress);
  const currencies = useGameStore(s => s.currencies);
  const state = (location.state as LocationState) || {
    modes: ['casual'], warStake: null, deckId: null, deckName: '—'
  };

  const [phase, setPhase] = useState<Phase>('scan');
  const [elapsed, setElapsed] = useState(1);
  const [found, setFound] = useState<{
    opponent: string; opponentFaction: string; foundIn: ModeId;
    // Этап 68.3: косметика соперника (цвет ника/аватар/титул) для VS-экрана.
    opponentAccent?: string | null;
    opponentAvatar?: string | null;
    opponentTitleCode?: string | null;
    opponentTitleText?: string | null;
    // МЧМ.3: для war — реальная ставка winning-комнаты (для UI pill'ов).
    foundStake?: number;
  } | null>(null);
  const [countdown, setCountdown] = useState<number>(3);

  // Главный цвет режима (для радара/глитча).
  const accent = useMemo(() => {
    if (state.modes.includes('war')) return '#FF3D3D';
    if (state.modes.includes('rated')) return '#FFB13D';
    return '#3DF0FF';
  }, [state.modes]);

  // Hard-cap для текущего набора режимов — берём максимальный из них
  // (на случай если casual+rated одновременно).
  const hardCapSec = useMemo(() => {
    return Math.max(...state.modes.map((m) => HARD_CAP_S[m]));
  }, [state.modes]);

  const timers = useRef<{
    elapsed: ReturnType<typeof setInterval> | null;
    phaseStep: ReturnType<typeof setTimeout> | null;
    countdown: ReturnType<typeof setInterval> | null;
  }>({ elapsed: null, phaseStep: null, countdown: null });

  // ПвП.4b: ссылка на текущую Colyseus-комнату. Не в стейте — не нужно
  // ререндерить экран на каждом её state-update'е.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const roomRef = useRef<Room<any> | null>(null);
  // МЧМ.3: все открытые room'ы (несколько очередей одновременно).
  // При успешном match — оставляем выигравшую в roomRef, остальным leave().
  const allRoomsRef = useRef<Array<{ room: Room<any>; spec: QueueSpec }>>([]);
  const navigatedRef = useRef(false);

  const clearAll = () => {
    const t = timers.current;
    if (t.elapsed) { clearInterval(t.elapsed); t.elapsed = null; }
    if (t.phaseStep) { clearTimeout(t.phaseStep); t.phaseStep = null; }
    if (t.countdown) { clearInterval(t.countdown); t.countdown = null; }
  };

  // ПвП.4b: маппинг фракций в i18n-метку для UI.
  const factionToLabel = (f?: string): string => {
    if (f === 'deus') return 'Деус';
    if (f === 'syntetics') return 'Синтетикс';
    if (f === 'global') return 'Глобал';
    if (f === 'livlife') return 'Лив Лайф';
    return FACTION_LABELS[Math.floor(Math.random() * FACTION_LABELS.length)];
  };

  // Старт поиска — реальный joinOrCreate.
  useEffect(() => {
    if (!state.modes || state.modes.length === 0) {
      navigate('/pvp', { replace: true });
      return;
    }
    // ВОЙ.3: фракция должна быть из 4 — нейтральная колода в PvP не играется.
    if (state.faction === 'neutral') {
      console.error('[matchmaking] neutral faction not allowed in PvP');
      navigate('/pvp', {
        replace: true,
        state: { matchmakingError: true, neutralDeckRejected: true }
      });
      return;
    }
    // МЧМ.3: собираем все очереди. modes × warStakes.
    //   casual → 1 очередь
    //   rated  → 1 очередь
    //   war + warStakes=[$1,$5,$10] → 3 очереди (war:1, war:5, war:10)
    // Приоритет при race: war:max > war:lesser > rated > casual.
    //   priority = stake*1000 для war, 5 для rated, 1 для casual.
    const warStakes = (state.warStakes && state.warStakes.length > 0)
      ? [...state.warStakes].sort((a, b) => b - a) // desc — больше первая
      : (state.warStake ? [state.warStake] : []);
    const specs: QueueSpec[] = [];
    for (const mode of state.modes) {
      if (mode === 'war') {
        if (warStakes.length === 0) continue;
        for (const stake of warStakes) {
          specs.push({
            mode: 'war',
            serverMode: `war:${stake}`,
            stake,
            league: '_war_',
            priority: 1000 + stake,
          });
        }
      } else if (mode === 'rated') {
        specs.push({
          mode: 'rated', serverMode: 'rated', stake: 0,
          league: progress.league || 'Трущобы',
          priority: 5,
        });
      } else {
        specs.push({
          mode: 'casual', serverMode: 'casual', stake: 0,
          league: '_casual_',
          priority: 1,
        });
      }
    }
    if (specs.length === 0) {
      navigate('/pvp', { replace: true });
      return;
    }
    // ВОЙ.2: pre-flight баланс — нужен MAX из выбранных ставок × 4
    // (фриз произойдёт только в одной очереди, по факту попадания).
    if (warStakes.length > 0) {
      const maxStake = warStakes[0];
      const need = maxStake * 4;
      const have = currencies?.usdt ?? 0;
      if (have < need) {
        navigate('/pvp', {
          replace: true,
          state: {
            matchmakingError: true,
            warFreezeFailed: true,
            blamedSelf: true,
            insufficientUsdt: { need, have }
          }
        });
        return;
      }
    }
    timers.current.elapsed = setInterval(() => setElapsed((e) => e + 1), 1000);

    let cancelled = false;

    // МЧМ.3: коллектор «комнат с найденным оппонентом». Когда любая из
    // активных очередей набирает 2-го игрока — добавляем сюда. Через 200мс
    // выбираем по приоритету (даём шанс другим очередям тоже выстрелить
    // если они в той же сетке тиков).
    const foundCandidates: Array<{ room: Room<any>; spec: QueueSpec; opp: any }> = [];
    let raceResolveTimer: ReturnType<typeof setTimeout> | null = null;

    const resolveRace = () => {
      raceResolveTimer = null;
      if (navigatedRef.current) return;
      if (foundCandidates.length === 0) return;
      // Берём с максимальным priority. При равенстве — первый.
      const winner = foundCandidates.reduce((best, cur) =>
        cur.spec.priority > best.spec.priority ? cur : best
      );
      navigatedRef.current = true;
      roomRef.current = winner.room;
      // Leave всех остальных room'ов.
      for (const { room } of allRoomsRef.current) {
        if (room !== winner.room) {
          room.leave().catch(() => {});
        }
      }
      console.log(
        `[matchmaking] race resolved → ${winner.spec.serverMode} ` +
        `(priority=${winner.spec.priority}, candidates=${foundCandidates.length})`
      );
      // Этап 68.3: косметика соперника — читаем из ЖИВОГО состояния комнаты
      // (сервер в startMatch мог дозагрузить её уже после первого snapshot).
      let liveOpp: any = winner.opp;
      try {
        const st: any = winner.room.state;
        const ps: any[] = st?.players?.toJSON?.() || st?.players || [];
        const meIdx = ps.findIndex((p: any) => p?.sessionId === winner.room.sessionId);
        const fresh = ps.find((p: any, i: number) => i !== meIdx);
        if (fresh) liveOpp = fresh;
      } catch { /* fallback на winner.opp */ }
      setFound({
        opponent: liveOpp.nick || 'Игрок',
        opponentFaction: factionToLabel(liveOpp.faction),
        opponentAccent: liveOpp.accentColor || null,
        opponentAvatar: liveOpp.avatarId || null,
        opponentTitleCode: liveOpp.titleCode || null,
        opponentTitleText: liveOpp.titleText || null,
        foundIn: winner.spec.mode,
        foundStake: winner.spec.stake || undefined,
      });
      setPhase('lock');
      play('mmFound'); // Этап 71: стинг «соперник найден»
      timers.current.phaseStep = setTimeout(() => {
        setPhase('vs');
        timers.current.phaseStep = setTimeout(() => {
          setPhase('countdown');
        }, 1200);
      }, 800);
    };

    const openOne = async (spec: QueueSpec) => {
      const opts = {
        nick: player.nick,
        faction: state.faction as 'deus' | 'syntetics' | 'global' | 'livlife',
        heroId: state.heroId || undefined,
        league: spec.league,
        mode: spec.serverMode,
        deckId: state.deckId ?? undefined,
      };
      const r = await joinOrCreateMatch(opts);
      if (cancelled || navigatedRef.current) {
        r.leave().catch(() => {});
        return;
      }
      allRoomsRef.current.push({ room: r, spec });

      const tryFound = () => {
        if (navigatedRef.current) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const st: any = r.state;
        const players: any[] = st?.players?.toJSON?.() || st?.players || [];
        const me = players.findIndex((p: any) => p?.sessionId === r.sessionId);
        if (me < 0) return;
        const opp = players.find((p: any, i: number) => i !== me);
        if (!opp) return;
        // Уже добавлено эта room'а в candidates?
        if (foundCandidates.some(c => c.room === r)) return;
        foundCandidates.push({ room: r, spec, opp });
        console.log(`[matchmaking] candidate from ${spec.serverMode} (priority=${spec.priority})`);
        // 200мс окно на сбор кандидатов из других очередей.
        if (!raceResolveTimer) {
          raceResolveTimer = setTimeout(resolveRace, 200);
        }
      };

      r.onStateChange(() => tryFound());
      // ВОЙ.2: war_freeze_failed может прилететь только от war-комнаты.
      // Ошибка → отменяем все queues и возвращаем на /pvp.
      r.onMessage('war_freeze_failed', (msg: any) => {
        console.error('[matchmaking] war freeze failed:', msg);
        if (!navigatedRef.current) {
          navigatedRef.current = true;
          const blamedSelf = msg?.blamedSid === r.sessionId;
          // Leave всех room'ов.
          for (const { room } of allRoomsRef.current) {
            room.leave().catch(() => {});
          }
          navigate('/pvp', {
            replace: true,
            state: {
              matchmakingError: true,
              warFreezeFailed: true,
              blamedSelf,
            }
          });
        }
      });
      // Задача «ошибка вместо фабричной колоды»: сервер не смог загрузить
      // реальную колоду кого-то из игроков → матч не стартует. Отменяем все
      // очереди и возвращаем на /pvp с понятной ошибкой.
      r.onMessage('deck_load_failed', (msg: any) => {
        console.error('[matchmaking] deck load failed:', msg);
        if (!navigatedRef.current) {
          navigatedRef.current = true;
          // blamedSid отсутствует (start_failed) → считаем виноватым себя.
          const blamedSelf = !msg?.blamedSid || msg.blamedSid === r.sessionId;
          for (const { room } of allRoomsRef.current) {
            room.leave().catch(() => {});
          }
          navigate('/pvp', {
            replace: true,
            state: {
              matchmakingError: true,
              deckLoadFailed: true,
              blamedSelf,
            }
          });
        }
      });
      r.onError((code: number, errMsg?: string) => {
        console.error(`[matchmaking] ${spec.serverMode} room error`, code, errMsg);
        // Не валим всё на ошибке одной очереди — может другие сработают.
      });
      r.onLeave(() => {
        // Если мы leave'нули сами в resolveRace — игнор. Иначе чистим.
        if (navigatedRef.current) return;
      });
      tryFound();
    };

    // Открываем все очереди параллельно.
    Promise.all(specs.map(spec => openOne(spec).catch(e => {
      console.error(`[matchmaking] ${spec.serverMode} open failed:`, e);
    })));

    return () => {
      cancelled = true;
      clearAll();
      if (raceResolveTimer) clearTimeout(raceResolveTimer);
      // Покидаем все комнаты ТОЛЬКО если ещё не передали выигравшую в /pvp/battle.
      if (!navigatedRef.current) {
        for (const { room } of allRoomsRef.current) {
          room.leave().catch(() => {});
        }
        allRoomsRef.current = [];
        roomRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hard-cap.
  useEffect(() => {
    if (phase !== 'scan') return;
    if (elapsed < hardCapSec) return;
    clearAll();
    navigate('/pvp', { replace: true, state: { matchmakingTimeout: true } });
  }, [elapsed, phase, hardCapSec, navigate]);

  // Этап 71: тихий радар-пинг, пока идёт поиск (раз в 2с).
  useEffect(() => {
    if (phase !== 'scan') return;
    const id = setInterval(() => play('mmPulse'), 2000);
    return () => clearInterval(id);
  }, [phase]);

  // Countdown 3..0.
  useEffect(() => {
    if (phase !== 'countdown') return;
    setCountdown(3);
    play('clockTick'); // Этап 71: тик на каждую цифру 3-2-1
    timers.current.countdown = setInterval(() => {
      setCountdown((c) => {
        if (c > 1) play('clockTick');
        return c - 1;
      });
    }, 1000);
    return () => {
      if (timers.current.countdown) {
        clearInterval(timers.current.countdown);
        timers.current.countdown = null;
      }
    };
  }, [phase]);

  // По 0 → переход в /pvp/battle с heavy_glitch.
  useEffect(() => {
    if (phase !== 'countdown') return;
    if (countdown > 0) return;
    clearAll();
    // ПвП.4b: передаём комнату через singleton — Room не сериализуется
    // через router state, а пересоздавать соединение нельзя (потеряем
    // sessionId и filtered view).
    setCurrentRoom(roomRef.current);
    tx.navigate('/pvp/battle', {
      replace: true,
      variant: 'heavy_glitch',
      state: {
        opponent: found?.opponent,
        opponentFaction: found?.opponentFaction,
        foundIn: found?.foundIn,
        deckId: state.deckId,
        deckName: state.deckName,
        // МЧМ.3: ставка победившей очереди (для UI EndOfMatchModal +
        // расчёта USDT). Если war не выиграл — null.
        warStake: found?.foundIn === 'war' ? (found?.foundStake ?? null) : null,
        // ПвП.6.1: маркер чтобы BattleScreen ушёл в PvP-ветку — берёт
        // комнату из getCurrentRoom(), не создаёт локальный engine.
        pvpMode: true,
      }
    });
  }, [countdown, phase, tx, found, state.deckId, state.deckName]);

  const onCancel = () => {
    clearAll();
    // МЧМ.3: при отмене покидаем ВСЕ комнаты во всех очередях.
    // Бот фолбэк отменится сам по disconnect'у.
    for (const { room } of allRoomsRef.current) {
      room.leave().catch(() => {});
    }
    allRoomsRef.current = [];
    roomRef.current = null;
    navigate('/pvp', { replace: true });
  };

  const mm = (n: number) => `00:${String(n).padStart(2, '0')}`;
  const isWar = state.modes.includes('war');
  // МЧМ.3: для UI блока депозита используем MAX из выбранных ставок
  // (фриз произойдёт только в одной, по факту попадания в room).
  const warStakesArr: number[] = state.warStakes && state.warStakes.length > 0
    ? state.warStakes
    : (state.warStake ? [state.warStake] : []);
  const stake = warStakesArr.length > 0 ? Math.max(...warStakesArr) : 0;
  const maxFreeze = stake * 4;
  const balanceOk = (currencies?.usdt ?? 0) >= maxFreeze;

  return (
    <ScreenLayout
      title={
        phase === 'scan'
          ? tr('ПОИСК БОЯ', 'MATCH SEARCH')
          : tr('ПРОТИВНИК НАЙДЕН', 'OPPONENT FOUND')
      }
      backTo={undefined as any}
    >
      <div className={styles.screen} style={{ ['--mm-accent' as string]: accent } as React.CSSProperties}>
        {/* Banner очередей. МЧМ.3: war с N ставками = N отдельных pill'ов. */}
        {(() => {
          // Считаем общее число очередей = casual/rated + по одной за каждую warStake.
          const queueCount =
            (state.modes.includes('casual') ? 1 : 0) +
            (state.modes.includes('rated') ? 1 : 0) +
            (state.modes.includes('war') ? warStakesArr.length : 0);
          return (
            <div className={styles.queuesBanner}>
              <div className={styles.queuesTitle}>
                {queueCount === 1
                  ? tr('ПОИСК В ОЧЕРЕДИ', 'SEARCHING IN QUEUE')
                  : tr(`ПОИСК В ${queueCount} ОЧЕРЕДЯХ ОДНОВРЕМЕННО`, `SEARCHING IN ${queueCount} QUEUES`)}
              </div>
              <div className={styles.queuesList}>
                {state.modes.includes('casual') && (() => {
                  const isFoundQ = !!found && found.foundIn === 'casual';
                  const isDropped = !!found && found.foundIn !== 'casual';
                  return (
                    <span
                      key="casual"
                      className={`${styles.queuePill} ${styles.casual} ${isFoundQ ? styles.found : ''} ${isDropped ? styles.dropped : ''}`}
                    >
                      {isFoundQ ? '✓ ' : ''}{modeLabel('casual', null, isEn)}
                    </span>
                  );
                })()}
                {state.modes.includes('rated') && (() => {
                  const isFoundQ = !!found && found.foundIn === 'rated';
                  const isDropped = !!found && found.foundIn !== 'rated';
                  return (
                    <span
                      key="rated"
                      className={`${styles.queuePill} ${styles.rated} ${isFoundQ ? styles.found : ''} ${isDropped ? styles.dropped : ''}`}
                    >
                      {isFoundQ ? '✓ ' : ''}{modeLabel('rated', null, isEn)}
                    </span>
                  );
                })()}
                {state.modes.includes('war') && warStakesArr.map((s) => {
                  const isFoundQ = !!found && found.foundIn === 'war' && found.foundStake === s;
                  const isDropped = !!found && !isFoundQ;
                  return (
                    <span
                      key={`war-${s}`}
                      className={`${styles.queuePill} ${styles.war} ${isFoundQ ? styles.found : ''} ${isDropped ? styles.dropped : ''}`}
                    >
                      {isFoundQ ? '✓ ' : ''}{modeLabel('war', s, isEn)}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* SCAN: радар */}
        {phase === 'scan' && (
          <div className={styles.radarBox}>
            <Radar accent={accent} danger={isWar} />
            <div className={styles.status}>
              {tr('СКАНИРОВАНИЕ СЕТИ…', 'SCANNING THE NET…')}
            </div>
            <div className={styles.elapsed}>{mm(elapsed)}</div>
            <div className={styles.hint}>
              {isWar ? tr(
                'Подбор по ставке. Бот в Войну не подключается — ждём живого соперника.',
                'Matchmaking is by stake. Bots don\'t join War — waiting for a live opponent.'
              ) : tr(
                'Подбор среди игроков того же режима. На малой аудитории может занять время.',
                'Matchmaking is within the same mode. On a small player base this may take time.'
              )}
            </div>
          </div>
        )}

        {/* LOCK / VS / COUNTDOWN: общий блок с фазами */}
        {phase !== 'scan' && found && (
          <div className={`${styles.vsBox} ${styles[`vsBox_${phase}`]}`}>
            <div className={`${styles.side} ${styles.sideLeft}`} style={{ color: 'var(--neon-am, #FFB13D)' }}>
              <div className={styles.ava}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="9" r="3.5" />
                  <path d="M5 20a7 7 0 0 1 14 0" />
                </svg>
              </div>
              <div className={styles.nick}>{player.nick || tr('Я', 'Me')}</div>
              <div className={styles.meta}>{trLeague(progress.league) || tr('Трущобы', 'Slums')}</div>
            </div>
            <div className={`${styles.vs} ${phase === 'countdown' ? styles.vsCountdown : ''}`}>
              {phase === 'countdown' ? (
                <span className={styles.cdNum} key={countdown}>
                  {countdown > 0 ? countdown : ''}
                </span>
              ) : 'VS'}
            </div>
            <div
              className={`${styles.side} ${styles.sideRight}`}
              style={{ color: FACTION_COLORS[found.opponentFaction] || 'var(--neon-rd, #FF3D3D)' }}
            >
              {/* Этап 68.3: аватар + ник с цветом/титулом соперника. */}
              <div className={styles.ava}>
                <UserAvatar avatarId={found.opponentAvatar} size={28} />
              </div>
              <div className={styles.nick}>
                <PlayerName
                  name={found.opponent}
                  accentColor={found.opponentAccent}
                  title={(found.opponentTitleCode || found.opponentTitleText)
                    ? { title_code: found.opponentTitleCode || '', title_text_ru: found.opponentTitleText || '' }
                    : null}
                  size="sm"
                  inline
                />
              </div>
              <div className={styles.meta}>{found.opponentFaction}</div>
            </div>
          </div>
        )}

        {/* Deck row */}
        <div className={styles.deckRow}>
          <span className={styles.lbl}>{tr('КОЛОДА', 'DECK')}</span>
          <span className={styles.nm}>{state.deckName}</span>
        </div>

        {/* Stake box (только для войны) */}
        {isWar && (
          <div className={styles.stakeBox}>
            <div className={styles.stakeBoxTitle}>⚠ {tr('ВОЙНА КОРПОРАЦИЙ — ДЕПОЗИТ', 'CORP WARS — DEPOSIT')}</div>
            <div className={styles.stakeGrid}>
              <span className="k">{tr('Начальная ставка', 'Starting stake')}</span>
              <span className={`${styles.v} ${styles.usdt}`}>{stake.toFixed(2)} USDT</span>
              <span className="k">{tr('Максимум при ×4', 'Max at ×4')}</span>
              <span className={`${styles.v} ${styles.usdt}`}>{maxFreeze.toFixed(2)} USDT</span>
              <span className="k">{tr('Замораживается на счёте', 'Frozen on balance')}</span>
              <span className={styles.v}>{maxFreeze.toFixed(2)} USDT</span>
              <span className="k">{tr('У тебя на счёте', 'Your balance')}</span>
              <span className={`${styles.v} ${balanceOk ? styles.ok : styles.danger}`}>
                {(currencies?.usdt ?? 0).toFixed(2)} USDT
              </span>
            </div>
            <div className={styles.stakeNote}>
              {tr(
                'В любой момент матча можно объявить Войну — ставка удваивается (макс ×4). Противник в свою Ф2 принимает или отступает за 10 сек. Комиссия платформы — 10% с выигрыша.',
                "Any time during the match you can declare War — stake doubles (max ×4). Opponent must accept or retreat within 10 sec in their P2. Platform fee — 10% of winnings."
              )}
            </div>
          </div>
        )}

        {/* Countdown label (под VS-блоком) */}
        {phase === 'countdown' && (
          <div className={`${styles.cdLabel} ${found?.foundIn === 'war' ? styles.danger : ''}`}>
            {tr('бой начнётся автоматически', 'battle starts automatically')}
          </div>
        )}

        {/* Cancel button — недоступна при countdown */}
        <div className={styles.actions}>
          <button
            className={styles.cancel}
            onClick={onCancel}
            disabled={phase === 'countdown'}
            style={{ opacity: phase === 'countdown' ? 0.4 : 1 }}
          >
            {tr('ОТМЕНА', 'CANCEL')}
          </button>
        </div>
      </div>
    </ScreenLayout>
  );
}

// ─── Радар-фон для phase=scan ─────────────────────────────────
function Radar({ accent, danger }: { accent: string; danger: boolean }) {
  // 12 точек-узлов с псевдо-рандомными позициями (стабильные через useMemo).
  const nodes = useMemo(
    () => Array.from({ length: 12 }, (_, i) => ({
      key: i,
      x: 10 + ((i * 73) % 80),    // 10..90 %
      y: 10 + ((i * 47) % 80),    // 10..90 %
      delay: (i % 5) * 0.6        // pulse delay
    })),
    []
  );
  return (
    <div className={`${styles.radar} ${danger ? styles.radarDanger : ''}`} style={{ color: accent }}>
      {/* Сетка координат */}
      <div className={styles.radarGrid} />
      {/* Концентрические кольца — 3 кольца с разной задержкой */}
      <div className={styles.radarRing} style={{ animationDelay: '0s' }} />
      <div className={styles.radarRing} style={{ animationDelay: '0.5s' }} />
      <div className={styles.radarRing} style={{ animationDelay: '1.0s' }} />
      {/* Точки-узлы */}
      {nodes.map((n) => (
        <div
          key={n.key}
          className={styles.radarNode}
          style={{
            left: `${n.x}%`, top: `${n.y}%`,
            animationDelay: `${n.delay}s`
          }}
        />
      ))}
      {/* Центральный пульс (свой аватар) */}
      <div className={styles.radarCenter} />
    </div>
  );
}
